import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getParentMenus } from "../../api/MenuService";
import {
  dropdownCategories,
  dropdownBlogsList,
  dropdownProducts,
} from "../../api/dropdownService";

export default function MenuForm({
  onSubmit,
  loading = false,
  isEdit = false,
  initialData = {},
  currentMenuId = "",
}) {
  const navigate = useNavigate();

  const initialState = {
    name: "",
    type: "",
    component: "",
    componentId: "",
    parentId: "",
    slug: "",
    visible: true,
    order: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Parent menus
  const [parentMenus, setParentMenus] = useState([]);
  const [parentLoading, setParentLoading] = useState(true);
  const [parentError, setParentError] = useState("");

  // Categories
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(true);

  // Products
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);

  // Blogs
  const [blogs, setBlogs] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);

  // Menu components
  const [menuComponents, setMenuComponents] = useState([]);

  // ============================================================
  // Get menu components from localStorage
  // ============================================================
  useEffect(() => {
    const storedMenuComponents = localStorage.getItem("menuComponents");

    if (storedMenuComponents) {
      try {
        const parsedComponents = JSON.parse(storedMenuComponents);

        if (Array.isArray(parsedComponents)) {
          setMenuComponents(parsedComponents);
        } else {
          setMenuComponents([]);
        }
      } catch (error) {
        console.error("Error parsing menuComponents:", error);
        setMenuComponents([]);
      }
    } else {
      setMenuComponents([]);
    }
  }, []);

  // ============================================================
  // Fetch parent menus
  // ============================================================
  useEffect(() => {
    const fetchParentMenus = async () => {
      try {
        setParentLoading(true);
        setParentError("");

        const response = await getParentMenus();

        const parents =
          response?.data?.parents ||
          response?.data?.menus ||
          response?.data?.data ||
          response?.parents ||
          response?.menus ||
          response?.data ||
          response ||
          [];

        setParentMenus(Array.isArray(parents) ? parents : []);
      } catch (error) {
        setParentMenus([]);

        setParentError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load parent menus"
        );
      } finally {
        setParentLoading(false);
      }
    };

    fetchParentMenus();
  }, []);

  // ============================================================
  // Fetch categories
  // ============================================================
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);

        const response = await dropdownCategories();

        const categoryList =
          response?.data?.categories ||
          response?.data?.data ||
          response?.categories ||
          response?.data ||
          response ||
          [];

        setCategories(
          Array.isArray(categoryList) ? categoryList : []
        );
      } catch (error) {
        console.error("Failed to load categories:", error);
        setCategories([]);
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // ============================================================
  // Fetch products
  // ============================================================
  const fetchProducts = async () => {
    try {
      setProductLoading(true);

      const response = await dropdownProducts();

      const productList =
        response?.data?.products ||
        response?.data?.data ||
        response?.products ||
        response?.data ||
        response ||
        [];

      setProducts(
        Array.isArray(productList) ? productList : []
      );
    } catch (error) {
      console.error("Failed to load products:", error);
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  };

  // ============================================================
  // Fetch blogs
  // ============================================================
  const fetchBlogs = async () => {
  try {
    setBlogLoading(true);

    const response = await dropdownBlogsList();

    const blogList =
      response?.data?.blogs ||
      response?.data?.data ||
      response?.blogs ||
      response?.data ||
      response ||
      [];

    setBlogs(Array.isArray(blogList) ? blogList : []);
  } catch (error) {
    console.error("Failed to load blogs:", error);
    setBlogs([]);
  } finally {
    setBlogLoading(false);
  }
};

  // ============================================================
  // Set initial data for edit
  // ============================================================
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const componentValue = initialData.component || "";

      const componentObject = menuComponents.find(
        (component) => component.label === componentValue
      );

      const componentRoute = componentObject?.value || "";

      let componentId = "";
      let parentId = "";

      if (typeof initialData.componentId === "object") {
        componentId = initialData.componentId?._id || "";
      } else {
        componentId = initialData.componentId || "";
      }

      if (typeof initialData.parentId === "object") {
        parentId = initialData.parentId?._id || "";
      } else {
        parentId = initialData.parentId || "";
      }

      setFormData({
        name: initialData.name || "",
        type: initialData.type || "",
        component: componentValue,
        componentId,
        parentId,
        slug:
          initialData.slug ||
          (componentRoute ? `${componentRoute}/` : ""),
        visible: initialData.visible !== false,
        order:
          initialData.order === null ||
          initialData.order === undefined
            ? ""
            : String(initialData.order),
      });

      // Load component lists when editing SUB menu
      if (initialData.type === "SUB") {
        if (componentValue === "Products") {
          fetchProducts();
        }

        if (componentValue === "Blog") {
          fetchBlogs();
        }
      }
    }
  }, [initialData, menuComponents]);

  // ============================================================
  // Get component route
  // ============================================================
  const getComponentRoute = (componentLabel) => {
    const selectedComponent = menuComponents.find(
      (component) => component.label === componentLabel
    );

    return selectedComponent?.value || "";
  };

  // ============================================================
  // Handle normal input/select changes
  // ============================================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // ==========================================================
    // TYPE CHANGE
    // ==========================================================
    if (name === "type") {
      setFormData((prev) => ({
        ...prev,
        type: value,
        componentId: "",
        parentId: "",
        slug: "",
      }));

      setErrors((prev) => ({
        ...prev,
        type: "",
        componentId: "",
        parentId: "",
        slug: "",
      }));

      return;
    }

    // ==========================================================
    // COMPONENT CHANGE
    // ==========================================================
    if (name === "component") {
      const componentRoute = getComponentRoute(value);

      setFormData((prev) => ({
        ...prev,
        component: value,
        componentId: "",
        slug: componentRoute ? `${componentRoute}/` : "",
      }));

      setErrors((prev) => ({
        ...prev,
        component: "",
        componentId: "",
        slug: "",
      }));

      // ========================================================
      // Fetch component list only when required
      //
      // MAIN:
      //   Category -> list required
      //   Products -> no list
      //   Blog     -> no list
      //
      // SUB:
      //   Category -> list
      //   Products -> list
      //   Blog     -> list
      // ========================================================

      if (value === "Products" && formData.type === "SUB") {
        fetchProducts();
      }

      if (value === "Blog" && formData.type === "SUB") {
        fetchBlogs();
      }

      return;
    }

    // ==========================================================
    // COMPONENT LIST SELECTION
    // ==========================================================

    if (name === "componentId") {
      const componentRoute = getComponentRoute(
        formData.component
      );

      // ========================================================
      // CATEGORY
      // ========================================================
      if (formData.component === "Category") {
        const selectedCategory = categories.find(
          (category) => category._id === value
        );

        const categorySlug =
          selectedCategory?.slug ||
          selectedCategory?.name
            ?.toLowerCase()
            ?.trim()
            ?.replace(/\s+/g, "-") ||
          "";

        const finalSlug =
          componentRoute && categorySlug
            ? `${componentRoute}/${categorySlug}`
            : `${componentRoute}/`;

        setFormData((prev) => ({
          ...prev,
          componentId: value,
          slug: finalSlug,
        }));

        setErrors((prev) => ({
          ...prev,
          componentId: "",
          slug: "",
        }));

        return;
      }

      // ========================================================
      // PRODUCTS
      // ========================================================
      if (formData.component === "Products") {
        const selectedProduct = products.find(
          (product) => product._id === value
        );

        const productSlug =
          selectedProduct?.slug ||
          selectedProduct?.name
            ?.toLowerCase()
            ?.trim()
            ?.replace(/\s+/g, "-") ||
          "";

        const finalSlug =
          componentRoute && productSlug
            ? `${componentRoute}/${productSlug}`
            : `${componentRoute}/`;

        setFormData((prev) => ({
          ...prev,
          componentId: value,
          slug: finalSlug,
        }));

        setErrors((prev) => ({
          ...prev,
          componentId: "",
          slug: "",
        }));

        return;
      }

      // ========================================================
      // BLOG
      // ========================================================
      if (formData.component === "Blog") {
        const selectedBlog = blogs.find(
          (blog) => blog._id === value
        );

        const blogSlug =
          selectedBlog?.slug ||
          selectedBlog?.name
            ?.toLowerCase()
            ?.trim()
            ?.replace(/\s+/g, "-") ||
          "";

        const finalSlug =
          componentRoute && blogSlug
            ? `${componentRoute}/${blogSlug}`
            : `${componentRoute}/`;

        setFormData((prev) => ({
          ...prev,
          componentId: value,
          slug: finalSlug,
        }));

        setErrors((prev) => ({
          ...prev,
          componentId: "",
          slug: "",
        }));

        return;
      }
    }

    // ==========================================================
    // OTHER INPUTS
    // ==========================================================
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ============================================================
  // Handle visible checkbox
  // ============================================================
  const handleVisibleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      visible: e.target.checked,
    }));

    setErrors((prev) => ({
      ...prev,
      visible: "",
    }));
  };

  // ============================================================
  // Check if Component List should be shown
  //
  // MAIN:
  //   Only Category
  //
  // SUB:
  //   Category
  //   Products
  //   Blog
  // ============================================================
  const showComponentList =
    (formData.type === "MAIN" &&
      formData.component === "Category") ||
    (formData.type === "SUB" &&
      formData.component);

  // ============================================================
  // Submit form
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setErrors({});

      const validationErrors = {};

      // ========================================================
      // BASIC VALIDATION
      // ========================================================
      if (!formData.name.trim()) {
        validationErrors.name = "Name is required";
      }

      if (!formData.type) {
        validationErrors.type = "Type is required";
      }

      if (!formData.component) {
        validationErrors.component = "Component is required";
      }

      // ========================================================
      // SLUG VALIDATION
      // ========================================================
      if (!formData.slug.trim()) {
        validationErrors.slug =
          "Selected component slug not found";
      }

      // ========================================================
      // SUB ONLY
      // ========================================================
      if (formData.type !== "MAIN") {
        if (!formData.componentId.trim()) {
          validationErrors.componentId =
            "Component ID is required";
        }

        if (!formData.parentId) {
          validationErrors.parentId =
            "Parent menu is required";
        }
      }

      // ========================================================
      // ORDER VALIDATION
      // ========================================================
      if (
        formData.order === "" ||
        Number.isNaN(Number(formData.order))
      ) {
        validationErrors.order =
          "Order must be a number";
      }

      // ========================================================
      // SHOW VALIDATION ERRORS
      // ========================================================
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      // ========================================================
      // API PAYLOAD
      // ========================================================
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        component: formData.component,
        slug: formData.slug.trim(),
        visible: Boolean(formData.visible),
        order: Number(formData.order),
      };

      // ========================================================
      // SUB ONLY
      //
      // MAIN does NOT send componentId / parentId
      // ========================================================
      if (formData.type === "SUB") {
        payload.componentId =
          formData.componentId.trim();

        payload.parentId = formData.parentId;
      }

      console.log("Menu Payload:", payload);

      // ========================================================
      // API SUBMIT
      // ========================================================
      const res = await onSubmit(payload);

      if (!res?.success) {
        const backendErrors = res?.error || {};

        setErrors({
          common:
            res?.message || "Validation failed",

          name: backendErrors?.name || "",
          type: backendErrors?.type || "",
          component: backendErrors?.component || "",
          componentId:
            backendErrors?.componentId || "",
          parentId:
            backendErrors?.parentId || "",
          slug: backendErrors?.slug || "",
          visible:
            backendErrors?.visible || "",
          order:
            backendErrors?.order || "",
        });

        return;
      }

      // ========================================================
      // SUCCESS
      // ========================================================
      setSuccessMessage(
        isEdit
          ? "Menu Updated Successfully"
          : "Menu Added Successfully"
      );

      if (!isEdit) {
        setFormData(initialState);
      }

      setTimeout(() => {
        setSuccessMessage("");
        navigate("/menu");
      }, 1500);
    } catch (error) {
      console.error("Menu submit error:", error);

      const responseData = error?.response?.data;

      setErrors({
        common:
          responseData?.message ||
          responseData?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div className="container-fluid p-4">
      <div className="card border-2 p-4">
        <h3>
          {isEdit ? "Edit Menu" : "Add Menu"}
        </h3>

        {/* Success Message */}
        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
          </div>
        )}

        {/* Common Error */}
        {errors.common && (
          <div className="alert alert-danger">
            {errors.common}
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* =====================================================
              ROW 1
          ====================================================== */}
          <div className="row h80 mb-2">

            {/* Name */}
            <div className="col-md-6">
              <label className="form-label">
                Name <span className="error">*</span>
              </label>

              <input
                type="text"
                name="name"
                maxLength={50}
                className="form-control"
                value={formData.name}
                onChange={handleChange}
              />

              {errors.name && (
                <div className="text-danger">
                  {errors.name}
                </div>
              )}
            </div>

            {/* Type */}
            <div className="col-md-6">
              <label className="form-label">
                Type <span className="error">*</span>
              </label>

              <select
                name="type"
                className="form-select"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="">
                  Select Type
                </option>

                <option value="MAIN">
                  MAIN
                </option>

                <option value="SUB">
                  SUB
                </option>

                {initialData.type &&
                  !["MAIN", "SUB"].includes(
                    initialData.type
                  ) && (
                    <option
                      value={initialData.type}
                    >
                      {initialData.type}
                    </option>
                  )}
              </select>

              {errors.type && (
                <div className="text-danger">
                  {errors.type}
                </div>
              )}
            </div>
          </div>

          {/* =====================================================
              ROW 2
          ====================================================== */}
          <div className="row h80 mb-2">

            {/* Component */}
            <div
              className={
                showComponentList
                  ? "col-md-6"
                  : "col-md-6"
              }
            >
              <label className="form-label">
                Component{" "}
                <span className="text-danger">
                  *
                </span>
              </label>

              <select
                name="component"
                className="form-select"
                value={formData.component}
                onChange={handleChange}
              >
                <option value="">
                  Select Component
                </option>

                {Array.isArray(menuComponents) &&
                  menuComponents.map(
                    (component, index) => (
                      <option
                        key={`${component.label}-${index}`}
                        value={component.label}
                      >
                        {component.label}
                      </option>
                    )
                  )}
              </select>

              {errors.component && (
                <div className="text-danger">
                  {errors.component}
                </div>
              )}
            </div>

            {/* =================================================
                COMPONENT LIST

                MAIN + Category
                SUB + Category
                SUB + Products
                SUB + Blog
            ================================================== */}
            {showComponentList && (
              <div className="col-md-6">

                <label className="form-label">
                  Component List{" "}
                  <span className="error">
                    *
                  </span>
                </label>

                {/* =============================================
                    CATEGORY LIST
                ============================================== */}
                {formData.component ===
                  "Category" && (
                  <>
                    <select
                      name="componentId"
                      className="form-select"
                      value={
                        formData.componentId
                      }
                      onChange={handleChange}
                      disabled={
                        categoryLoading
                      }
                    >
                      <option value="">
                        {categoryLoading
                          ? "Loading categories..."
                          : "Select Category"}
                      </option>

                      {categories.map(
                        (category) => (
                          <option
                            key={
                              category._id
                            }
                            value={
                              category._id
                            }
                          >
                            {category.name}
                          </option>
                        )
                      )}
                    </select>
                  </>
                )}

                {/* =============================================
                    PRODUCTS LIST
                ============================================== */}
                {formData.component ===
                  "Products" && (
                  <>
                    <select
                      name="componentId"
                      className="form-select"
                      value={
                        formData.componentId
                      }
                      onChange={handleChange}
                      disabled={
                        productLoading
                      }
                    >
                      <option value="">
                        {productLoading
                          ? "Loading products..."
                          : "Select Product"}
                      </option>

                      {products.map(
                        (product) => (
                          <option
                            key={
                              product._id
                            }
                            value={
                              product._id
                            }
                          >
                            {product.name ||
                              product.title ||
                              product.slug}
                          </option>
                        )
                      )}
                    </select>
                  </>
                )}

                {/* =============================================
                    BLOG LIST
                ============================================== */}
                {formData.component ===
                  "Blog" && (
                  <>
                    <select
                      name="componentId"
                      className="form-select"
                      value={
                        formData.componentId
                      }
                      onChange={handleChange}
                      disabled={
                        blogLoading
                      }
                    >
                      <option value="">
                        {blogLoading
                          ? "Loading blogs..."
                          : "Select Blog"}
                      </option>

                      {blogs.map(
                        (blog) => (
                          <option
                            key={
                              blog._id
                            }
                            value={
                              blog._id
                            }
                          >
                            {blog.name ||
                              blog.title ||
                              blog.slug}
                          </option>
                        )
                      )}
                    </select>
                  </>
                )}

                {errors.componentId && (
                  <div className="text-danger">
                    {errors.componentId}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* =====================================================
              ROUTE SLUG
              SHOW AFTER COMPONENT IS SELECTED
          ====================================================== */}
          {formData.component && (
            <div className="row mb-2">
              <div className="col-md-6">

                <label className="form-label">
                  Route Slug
                </label>

                <input
                  type="text"
                  name="slug"
                  className="form-control"
                  value={formData.slug}
                  readOnly
                  placeholder="Select component list"
                />

                {errors.slug && (
                  <div className="text-danger">
                    {errors.slug}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* =====================================================
              ROW 3
          ====================================================== */}
          <div className="row h80 mb-2">

            {/* Parent Menu */}
            {formData.type !== "MAIN" && (
              <div className="col-md-6">

                <label className="form-label">
                  Parent Menu{" "}
                  <span className="error">
                    *
                  </span>
                </label>

                <select
                  name="parentId"
                  className="form-select"
                  value={formData.parentId}
                  onChange={handleChange}
                  disabled={parentLoading}
                >
                  <option value="">
                    {parentLoading
                      ? "Loading parent menus..."
                      : "Select Parent Menu"}
                  </option>

                  {parentMenus.map(
                    (parent) => {
                      const parentId =
                        parent?._id ||
                        parent?.id ||
                        parent?.menuId;

                      const parentLabel =
                        parent?.name ||
                        parent?.label ||
                        parent?._id ||
                        parentId;

                      return (
                        <option
                          key={parentId}
                          value={parentId}
                        >
                          {parentLabel}
                        </option>
                      );
                    }
                  )}
                </select>

                {parentError && (
                  <div className="text-danger">
                    {parentError}
                  </div>
                )}

                {errors.parentId && (
                  <div className="text-danger">
                    {errors.parentId}
                  </div>
                )}
              </div>
            )}

            {/* Visible */}
            <div
              className={
                formData.type !== "MAIN"
                  ? "col-md-3"
                  : "col-md-6"
              }
            >
              <label className="form-label">
                Visible
              </label>

              <div className="form-check mt-2">

                <input
                  type="checkbox"
                  id="menu-visible"
                  name="visible"
                  className="form-check-input"
                  checked={
                    formData.visible
                  }
                  onChange={
                    handleVisibleChange
                  }
                  style={{
                    appearance: "auto",
                    WebkitAppearance:
                      "checkbox",
                    cursor: "pointer",
                  }}
                />

                <label
                  htmlFor="menu-visible"
                  className="form-check-label"
                  style={{
                    cursor: "pointer",
                  }}
                >
                  Visible
                </label>
              </div>
            </div>

            {/* Order */}
            <div
              className={
                formData.type !== "MAIN"
                  ? "col-md-3"
                  : "col-md-6"
              }
            >
              <label className="form-label">
                Order{" "}
                <span className="error">
                  *
                </span>
              </label>

              <input
                type="number"
                name="order"
                className="form-control"
                value={formData.order}
                onChange={handleChange}
                min="0"
              />

              {errors.order && (
                <div className="text-danger">
                  {errors.order}
                </div>
              )}
            </div>
          </div>

          {/* =====================================================
              BUTTONS
          ====================================================== */}
          <div className="d-flex justify-content-end gap-2 mt-3">

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? "Submitting..."
                : isEdit
                ? "Update"
                : "Submit"}
            </button>

            <button
              type="button"
              className="btn"
              style={{
                backgroundColor: "#032B4D",
                color: "#fff",
              }}
              onClick={() =>
                navigate("/menu")
              }
            >
              Cancel
            </button>

          </div>
        </form>
      </div>
    </div>
  );
}